import React, { useContext, useEffect, useState } from 'react';
import { FormDataContext } from '../../contexts/FormDataContext';
import { Alert, Box, Tooltip } from '@mui/material';
import axios from 'axios';
import { Submit } from '../Submit';
import useDebounce from '../../utilities/useDebounce';

const PREFERENCES_SUBMIT_URL = "/roa/intake/preferences/submit";

const STATUS_SUCCESS = 'SUCCESS';

export const PreferencesForm = ({children}) => {
  const [formDisabled, setFormDisabled] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');

  const {
    selectedAttributes,
    selectedDegrees,
    selectedLocationsAll,
    selectedLocations,
    selectedOccupations,
    selectedSchools,
    currentUserEmail,
    preferencesFormDisabled,
    setIsSubmitComplete,
    isFormDataChanged,
    setIsFormDataChanged,
    isFormCompleted,
    isLocationsAllSelected,
  } = useContext(FormDataContext);

  const handleAutoSave = useDebounce(() => {
      const formData = getFormObject();
      formData.autoSave = true;
      axios.post(PREFERENCES_SUBMIT_URL, formData)
        .then(response => {
          const saveResponseData = response.data;
          const responseStatus = saveResponseData.status;
          if (responseStatus !== STATUS_SUCCESS) {
            console.error(saveResponseData.message);
          }
        })
        .catch(error => console.error(error));
  }, 2000); // Debounce period in milliseconds

  useEffect(() => {
    if (!isFormCompleted && isFormDataChanged) {
      handleAutoSave();
      setIsFormDataChanged(false)
    }
  }, [isFormDataChanged]);

  useEffect(() => {
    if (formDisabled && currentUserEmail) {
      setFormDisabled(false);
    }
  }, [currentUserEmail]);

  const getFormObject = () => {
    return {
      email: currentUserEmail,
      attributes: selectedAttributes,
      education: {
        schools: selectedSchools,
        degrees: selectedDegrees,
      },
      locationsAll: selectedLocationsAll,
      locationsAllSelected: isLocationsAllSelected,
      locations: selectedLocations,
      occupations: selectedOccupations,
    };
  };

  const applyNavbarBodyClass = className => {
    const body = document.body;
    const classes = Array.from(body.classList);
    const classesToRemove = classes.filter(className => className.startsWith('header-navbar'));
    classesToRemove.forEach(className => body.classList.remove(className));
    body.classList.add(className);
  };

  const scrollToHeading = () => {
    setTimeout(() => {
      const h2Element = document.querySelector('h2');
      const header = document.querySelector('header');
      const headerHeight = header ? header.clientHeight : 0;
      if (h2Element) {
        const yOffset = -headerHeight;
        const y = h2Element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 200);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    setSubmitLoading(true);
    setSubmitErrorMessage('');
    try {
      // await new Promise((resolve) => setTimeout(resolve, 1500));
      const response = await axios.post(PREFERENCES_SUBMIT_URL, getFormObject());
      const submitResponseData = response.data;
      const responseStatus = submitResponseData.status;
      switch (responseStatus) {
        case STATUS_SUCCESS:
          setIsSubmitComplete(true);
          applyNavbarBodyClass('header-navbar-logout');
          scrollToHeading();
          break;
        default:
          setSubmitErrorMessage("An error occurred while saving your preferences. Please try again.")
          break;
      }
    } catch (error) {
      console.error("Submit failed - an error occurred:", error);
      setSubmitErrorMessage("An error occurred while saving your preferences. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box position="relative">
      <Box
        component="form"
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
        sx={{ opacity: preferencesFormDisabled ? 0.5 : 1 }}
      >
        {children}
        {submitErrorMessage &&
          <Alert sx={{marginY: '4px'}} id="email-message" severity="error">
            {submitErrorMessage}
          </Alert>
        }
        <Submit preferencesSaving={submitLoading}/>
      </Box>
      {preferencesFormDisabled && (
        <Tooltip title="Please provide your email address to continue." followCursor>
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            bgcolor="transparent"
            zIndex="tooltip" // Ensure overlay is above form elements
            display="flex"
            justifyContent="center"
            alignItems="center"
            color="white"
          />
        </Tooltip>
      )}
    </Box>
  );
};